/**
 * useQcfFont — Dynamic QCF font loader hook
 * Loads per-page QCF V2/V4 fonts from Quran Foundation CDN.
 * Each Mushaf page (1-604) has its own font file.
 */

import { useState, useEffect, useRef } from 'react';
import type { QuranWord } from '@/types/quran';

const CDN_BASE = 'https://verses.quran.foundation/fonts/quran/hafs';

// Global cache — survives across component mounts
const loadedFonts = new Set<string>();

/**
 * Get CDN URL for a font file
 */
function getFontUrl(pageNumber: number, version: 'v2' | 'v4', theme: 'light' | 'dark' | 'sepia' = 'light'): string {
  if (version === 'v4') {
    const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.includes('Firefox');
    if (isFirefox && theme !== 'light') {
      return `${CDN_BASE}/v4/ot-svg/${theme}/woff2/p${pageNumber}.woff2`;
    }
    return `${CDN_BASE}/v4/colrv1/woff2/p${pageNumber}.woff2`;
  }
  return `${CDN_BASE}/v2/woff2/p${pageNumber}.woff2`;
}

/**
 * Load a single page font via FontFace API
 */
async function loadPageFont(
  pageNumber: number,
  version: 'v2' | 'v4' = 'v2',
  theme: 'light' | 'dark' | 'sepia' = 'light'
): Promise<string> {
  const fontName = `p${pageNumber}-${version}`;
  if (loadedFonts.has(fontName)) return fontName;

  try {
    const url = getFontUrl(pageNumber, version, theme);
    const fontFace = new FontFace(fontName, `url('${url}')`);
    fontFace.display = 'block';
    await fontFace.load();
    document.fonts.add(fontFace);
    loadedFonts.add(fontName);
  } catch (error) {
    console.error(`Failed to load font for page ${pageNumber}:`, error);
  }

  return fontName;
}

/**
 * Hook: Load QCF fonts for all pages referenced by words
 * Returns a Set of page numbers whose fonts are loaded.
 */
export function useQcfFont(
  words: QuranWord[],
  options?: { version?: 'v2' | 'v4'; theme?: 'light' | 'dark' | 'sepia' }
): Set<number> {
  const version = options?.version ?? 'v2';
  const theme = options?.theme ?? 'light';
  const [loadedPages, setLoadedPages] = useState<Set<number>>(() => {
    // Initialize with already-loaded pages
    const initial = new Set<number>();
    words.forEach(w => {
      if (w.pageNumber && loadedFonts.has(`p${w.pageNumber}-${version}`)) {
        initial.add(w.pageNumber);
      }
    });
    return initial;
  });
  const prevWordsRef = useRef<QuranWord[]>([]);

  useEffect(() => {
    if (!words?.length) return;

    // Collect unique page numbers that need loading
    const pageNumbers = new Set<number>();
    words.forEach(w => {
      if (w.pageNumber) pageNumbers.add(w.pageNumber);
    });

    // Skip if nothing new to load
    const needsLoad = [...pageNumbers].some(p => !loadedFonts.has(`p${p}-${version}`));
    if (!needsLoad && prevWordsRef.current === words) {
      // All fonts already loaded
      setLoadedPages(new Set(pageNumbers));
      return;
    }
    prevWordsRef.current = words;

    let cancelled = false;
    const load = async () => {
      await Promise.all(
        [...pageNumbers].map(p => loadPageFont(p, version, theme))
      );
      if (!cancelled) {
        setLoadedPages(new Set(pageNumbers));
      }
    };
    load();

    return () => { cancelled = true; };
  }, [words, version, theme]);

  return loadedPages;
}

/**
 * Preload fonts for adjacent pages (for smooth navigation)
 */
export function preloadAdjacentPageFonts(
  currentPage: number,
  version: 'v2' | 'v4' = 'v2',
  theme: 'light' | 'dark' | 'sepia' = 'light'
): void {
  const pages = [currentPage - 1, currentPage + 1].filter(p => p >= 1 && p <= 604);
  pages.forEach(p => {
    if (!loadedFonts.has(`p${p}-${version}`)) {
      loadPageFont(p, version, theme);
    }
  });
}

/**
 * Check if a specific page font is loaded (for synchronous checks)
 */
export function isPageFontLoaded(pageNumber: number, version: 'v2' | 'v4' = 'v2'): boolean {
  return loadedFonts.has(`p${pageNumber}-${version}`);
}
